import { AppImage } from '@/components/ui/app-image';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Bookmark, Check, Send, X } from 'lucide-react-native';
import React from 'react';
import { Pressable, View } from 'react-native';
import Animated, { FadeIn, ZoomIn } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { CheckInSuccessData } from '../types';
import { RATING_CARD_CONFIG } from './rating-button-group';

export interface CheckInSuccessScreenProps {
  data: CheckInSuccessData;
  onDismiss: () => void;
  onSaveToList: () => void;
  onRecommend: () => void;
}

const FALLBACK_COLOR = '#9ACD32';

export function CheckInSuccessScreen({
  data,
  onDismiss,
  onSaveToList,
  onRecommend,
}: CheckInSuccessScreenProps) {
  const ratingConfig = RATING_CARD_CONFIG[data.ratingKey];
  const ratingColor = ratingConfig?.barColor ?? FALLBACK_COLOR;
  const ratingLabel = ratingConfig?.label ?? data.ratingKey;

  return (
    <SafeAreaView className="flex-1 bg-background">
      {/* Close button */}
      <View className="flex-row justify-end px-2 pt-1">
        <Pressable
          onPress={onDismiss}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          className="p-3"
          accessibilityRole="button"
          accessibilityLabel="Close"
        >
          <X size={24} color="#A8A49C" />
        </Pressable>
      </View>

      {/* Centered content */}
      <View className="flex-1 items-center justify-center px-6">
        {/* Checkmark circle */}
        <Animated.View
          entering={ZoomIn.delay(200).springify()}
          style={{
            width: 96,
            height: 96,
            borderRadius: 48,
            backgroundColor: ratingColor + '18',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Check size={44} color={ratingColor} strokeWidth={2.5} />
        </Animated.View>

        {/* Rating label + LOGGED */}
        <Animated.View
          entering={FadeIn.delay(400)}
          style={{ marginTop: 20 }}
        >
          <Text
            className="text-sm font-semibold text-center uppercase"
            style={{ color: ratingColor, letterSpacing: 3 }}
          >
            {ratingLabel}  {'·'}  LOGGED
          </Text>
        </Animated.View>

        {/* Menu item card */}
        <Animated.View
          entering={FadeIn.delay(550).duration(400)}
          style={{ width: '100%', marginTop: 32 }}
        >
          <Card className="flex-row items-center p-4">
            {data.coverPhotoR2Key ? (
              <View style={{ borderRadius: 10, borderCurve: 'continuous', overflow: 'hidden' }}>
                <AppImage
                  r2Key={data.coverPhotoR2Key}
                  preset="thumbnail"
                  style={{ width: 64, height: 64 }}
                  accessibilityLabel={`Photo of ${data.menuItemName}`}
                />
              </View>
            ) : (
              <View
                className="bg-surface-secondary items-center justify-center"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 10,
                  borderCurve: 'continuous',
                }}
              />
            )}
            <View className="flex-1 ml-3">
              <Text
                className="text-lg font-semibold text-foreground"
                numberOfLines={2}
              >
                {data.menuItemName}
              </Text>
              <Text
                className="text-sm text-muted-foreground mt-0.5"
                numberOfLines={1}
              >
                at {data.businessName}
              </Text>
            </View>
          </Card>
        </Animated.View>

        {/* Action buttons */}
        <Animated.View
          entering={FadeIn.delay(700)}
          style={{
            flexDirection: 'row',
            width: '100%',
            marginTop: 24,
            gap: 12,
          }}
        >
          <View className="flex-1">
            <Button variant="outline" fullWidth onPress={onSaveToList}>
              <Bookmark size={18} color="#A8A49C" />
              <Text className="text-foreground font-semibold text-base">
                Save To List
              </Text>
            </Button>
          </View>
          <View className="flex-1">
            <Button variant="default" fullWidth onPress={onRecommend}>
              <Send size={18} color="#080706" />
              <Text className="text-primary-foreground font-semibold text-base">
                Recommend
              </Text>
            </Button>
          </View>
        </Animated.View>
      </View>
    </SafeAreaView>
  );
}
